
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, HeadphonesIcon, Zap, ChevronDown } from "lucide-react";
import { type RegistrationFlowType } from "@/hooks/use-registration-flow";
import { useState, useEffect } from "react";

interface CredenciamentoTypeSelectorProps {
  selectedType: RegistrationFlowType;
  onTypeSelect: (type: RegistrationFlowType) => void;
}

export function CredenciamentoTypeSelector({ 
  selectedType, 
  onTypeSelect 
}: CredenciamentoTypeSelectorProps) {
  // Add a transitioning state to manage animations
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showExpressOnly, setShowExpressOnly] = useState(false);
  const [showAssistedOnly, setShowAssistedOnly] = useState(false);
  
  // Handle transitions when selection changes
  useEffect(() => {
    if (selectedType) {
      setIsTransitioning(true);
      
      // Set which card to show based on selection
      if (selectedType === "express") {
        setShowExpressOnly(true);
        setShowAssistedOnly(false);
      } else if (selectedType === "assisted") {
        setShowExpressOnly(false);
        setShowAssistedOnly(true);
      }
    } else {
      // Reset when no selection
      setIsTransitioning(false);
      setShowExpressOnly(false);
      setShowAssistedOnly(false);
    }
  }, [selectedType]);
  
  // Handle card selection
  const handleCardSelect = (type: RegistrationFlowType) => {
    if (selectedType === type) return;
    onTypeSelect(type);
  };

  return (
    <div className={`grid md:grid-cols-${(!selectedType || selectedType === null) ? '2' : '1'} gap-6 max-w-3xl mx-auto transition-all duration-500`}>
      {(!selectedType || selectedType === "express" || !isTransitioning) && (
        <Card 
          className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
            selectedType === "express" ? "ring-2 ring-drcash-caribbean-green animate-fade-in" : ""
          } ${showExpressOnly ? "md:col-span-2 mx-auto max-w-lg" : ""}`}
          onClick={() => handleCardSelect("express")}
        >
          <CardContent className="p-8 flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-drcash-caribbean-green/10 rounded-full flex items-center justify-center mb-5">
              <Zap 
                className={`w-10 h-10 ${
                  selectedType === "express" ? "text-drcash-caribbean-green" : "text-gray-500"
                }`} 
              />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-drcash-maastricht-blue">
              Credenciamento expresso
              {selectedType === "express" && (
                <Check className="w-6 h-6 inline-block ml-2 text-drcash-caribbean-green" />
              )}
            </h3>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">
              Mais rápido e você faz sozinho em minutos. Processo 100% online.
            </p>
            
            {selectedType === "express" ? (
              <div className="flex flex-col items-center mt-2">
                <p className="text-drcash-caribbean-green text-sm font-medium mb-2">
                  Role para baixo para continuar
                </p>
                <div className="animate-bounce">
                  <ChevronDown className="h-8 w-8 text-drcash-caribbean-green" />
                </div>
              </div>
            ) : (
              <Button 
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardSelect("express");
                }}
                className="min-w-32"
              >
                Selecionar
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {(!selectedType || selectedType === "assisted" || !isTransitioning) && (
        <Card 
          className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
            selectedType === "assisted" ? "ring-2 ring-drcash-caribbean-green animate-fade-in" : ""
          } ${showAssistedOnly ? "md:col-span-2 mx-auto max-w-lg" : ""}`}
          onClick={() => handleCardSelect("assisted")}
        >
          <CardContent className="p-8 flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-drcash-caribbean-green/10 rounded-full flex items-center justify-center mb-5">
              <HeadphonesIcon 
                className={`w-10 h-10 ${
                  selectedType === "assisted" ? "text-drcash-caribbean-green" : "text-gray-500"
                }`} 
              />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-drcash-maastricht-blue">
              Credenciamento assistido
              {selectedType === "assisted" && (
                <Check className="w-6 h-6 inline-block ml-2 text-drcash-caribbean-green" />
              )}
            </h3>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">
              Nosso consultor vai entrar em contato para ajudar você durante todo o processo.
            </p>
            
            {selectedType === "assisted" ? (
              <div className="flex flex-col items-center mt-2">
                <p className="text-drcash-caribbean-green text-sm font-medium">
                  Consultores entrarão em contato
                </p>
              </div>
            ) : (
              <Button 
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardSelect("assisted");
                }}
                className="min-w-32"
              >
                Selecionar
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
